import type { AgentUseCase, UseCaseContent } from "./types";

const en: UseCaseContent = {
  industry: "Financial services & regulated onboarding",
  title: "Onboarding & KYC agent",
  cardSummary:
    "Check that identity documents, proof of address and application details are consistent.",
  heroSubhead:
    "We design, build and deploy an AI agent that classifies each onboarding document, routes it to the right check, reconciles identity, address and the application form, and hands your compliance officer a clean, evidenced KYC packet — while the accept-or-refer decision stays with a human.",
  snapshot: {
    valueLabel: "High",
    valueWhy:
      "Onboarding is where deals stall and where AML/CDD risk enters — faster, consistent verification wins customers you lose to slow sign-up and closes the gaps a BNM or MAS audit looks for.",
    complexityLabel: "Medium–high",
    complexityWhy:
      "A routing front-end into per-document checks, vision/OCR on MyKad/NRIC and utility bills, sanctions/PEP lookups, and BNM/MAS AML/CFT + PDPA/PDPC-grade controls and audit trail.",
    timeToLive: "~a few weeks (one onboarding flow)",
    bestFit: "Banks, fintechs, insurers & any regulated business onboarding new customers",
  },
  outcomes: [
    "Win customers you lose today to slow, manual sign-up",
    "Catch document and identity mismatches before an account opens",
    "The same CDD checks applied consistently to every applicant",
    "More onboardings cleared without adding compliance headcount",
  ],
  impact: {
    manualLabel: "Manual KYC review today",
    manualValue: "~20–30 min",
    agentLabel: "With the agent",
    agentValue: "~4–7 min",
    saved:
      "≈ 15–23 minutes saved per applicant — about 75–115 hours a month at 300 onboardings, roughly half a full-time reviewer. And every applicant it clears in minutes instead of days is a customer you keep instead of losing to a competitor's faster sign-up.",
    note: "Indicative; actual time depends on the number of documents, screening depth and case complexity.",
  },
  businessProblem:
    "A bank, insurer or fintech in the region onboards each new customer as a bundle — MyKad/NRIC or passport, a selfie, proof of address, and the application form — then a compliance officer has to verify identity, confirm the address, screen against sanctions and PEP lists, and check every field agrees before an account can open. Most of that time goes to the mechanical part: classifying documents and matching them against each other, not judging risk. It is slow, applicants drop off while they wait, and two officers apply slightly different diligence to the same file — exactly the inconsistency a BNM or MAS AML/CFT review flags.",
  manualWorkflow: {
    steps: [
      "Open the onboarding bundle and each document",
      "Work out what each document is and read the fields by eye",
      "Cross-check name, IC/passport number and address across documents",
      "Screen the applicant against sanctions and PEP lists",
      "Chase missing documents, poor scans or mismatches",
      "Only then make the accept, refer or reject call",
    ],
    breaks:
      "The bottleneck is classifying and matching documents, not judging risk — the slow, repetitive part that clogs at volume, drifts as fatigue sets in, and varies between officers.",
  },
  agenticWorkflow: {
    narrative:
      "We build it as a routing workflow: the agent classifies each incoming document, dispatches it to the right specialised check (identity, address, screening), then reconciles the results into one evidenced KYC packet — re-requesting only what's genuinely missing and escalating anything it can't clear to a human.",
    steps: [
      "Classify each document (MyKad/NRIC, passport, bill, form)",
      "Route to the right check (identity / address / screening)",
      "Extract & verify the fields that matter",
      "Cross-match & screen against sanctions/PEP lists",
      "Assemble an evidenced KYC packet for the officer",
    ],
    inLabel: "An applicant submits their documents",
    outLabel: "A decision-ready KYC packet",
    loopAround: {
      index: 1,
      label: "Re-routes and re-requests only when a document is misclassified, unreadable or missing — otherwise it keeps moving.",
    },
    humanStep: "The compliance officer makes the accept / refer / reject decision",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "In the onboarding bundle",
    inMessage:
      "Application form: address 12, Jalan Ampang, 50450 Kuala Lumpur. MyKad/NRIC: Ahmad bin Ismail, 880101-14-5523. Utility bill (TNB): 88, Jalan Bukit Bintang, 55100 Kuala Lumpur.",
    outLabel: "What the agent flags",
    outLines: [
      "Name & IC number match across MyKad/NRIC and the application form",
      "Sanctions & PEP screening: no match",
      "Proof-of-address document present and legible",
    ],
    outResult:
      "1 discrepancy flagged: address on the form (Jalan Ampang) does not match the utility bill (Jalan Bukit Bintang)",
    note: "Every flag cites the exact document & field · your officer makes the accept/refer call · ~RM 0.50–1.10 per applicant.",
  },
  workflowDetail: [
    {
      title: "Classify each document",
      detail:
        "The agent reads every file in the bundle and works out what each one is — MyKad/NRIC, passport, selfie, utility bill, bank statement, application form — including scans and phone photos, so the right checks run on the right documents.",
    },
    {
      title: "Route to the right check",
      detail:
        "Each document is dispatched to its specialised handler: identity documents to identity verification, address proofs to address matching, the applicant's details to sanctions and PEP screening — the routing that keeps each check focused and accurate.",
    },
    {
      title: "Extract and verify fields",
      detail:
        "It pulls the fields that matter into a structured record — name, IC/passport number, date of birth, address, nationality — tagging the source document for each, and checking documents are legible and in date.",
    },
    {
      title: "Cross-match and screen",
      detail:
        "It reconciles those fields against each other — name and IC on MyKad/NRIC vs the form, address on the bill vs the form — and screens the applicant against sanctions and PEP watchlists, keeping the evidence for each result.",
    },
    {
      title: "Assemble the KYC packet",
      detail:
        "It produces a one-page packet: the applicant, the checks that passed, the screening result, and the two or three items to look at — each with the detail and the document it came from.",
    },
    {
      title: "Hand to a human",
      detail:
        "The compliance officer reviews the flagged items and makes the accept, refer or reject decision. Nothing is auto-approved.",
    },
  ],
  architecture: [
    "Pattern: a routing workflow on the Claude Agent SDK — a classifier front-end dispatches each document to a per-type check chain, each tool exposed via an in-process MCP server.",
    "Context: the whole bundle is loaded — text plus vision for MyKad/NRIC, passports and utility-bill photos — with the applicant's live case record pulled fresh (not a stale copy) and long submissions compacted so nothing is silently dropped.",
    "Tools: a document classifier, a vision/OCR field extractor, an address/identity matcher, and a read-only sanctions/PEP screening lookup — read tools separated from any write, which goes to the review queue only.",
    "Guardrails: schema-locked JSON output, no write access to the core banking / policy admin system, least-privilege, and a hard rule that no account is opened without a human accept decision.",
    "Untrusted input: every submitted document is treated as untrusted (prompt-injection resistant) — a doctored PDF or a caption inside an image can't steer the agent's classification or decision.",
    "Recovery & escalation: a misclassified, unreadable or missing document is re-routed or re-requested; low screening confidence or an ambiguous match escalates to the officer — it never guesses an identity or a screening result.",
    "Observability & audit: every classification, tool call, screening hit and the final packet are logged and traceable, with the officer's decision captured against it — a machine-readable BNM/MAS/PDPA audit trail, not a screenshot.",
    "Eval harness: a golden set of historical onboardings — including impersonation attempts, mismatched addresses and true sanctions hits — with deterministic checks plus an LLM-as-judge on the fuzzy calls, regression-tested on every change before it touches a live applicant.",
  ],
  models: [
    {
      task: "Classify documents & extract fields (incl. scans)",
      model: "Claude Sonnet",
      why: "Reliable vision and structured extraction on messy MyKad/NRIC photos, passports and utility bills.",
    },
    {
      task: "Route high-volume, clear-cut documents",
      model: "Claude Haiku",
      why: "Cheapest and fastest for the bulk of obvious classifications and simple field checks.",
    },
    {
      task: "Reconcile & reason across the bundle",
      model: "Claude Sonnet",
      why: "The workhorse — strong reasoning at moderate cost; handles cross-matching and screening review.",
    },
    {
      task: "Adjudicate ambiguous / borderline cases",
      model: "Claude Opus",
      why: "Invoked only when Sonnet flags low confidence or a possible screening match — a small fraction of applicants.",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI usage — per applicant",
        value: "~RM 0.50–1.10",
        note: "input documents + classification + reasoning + output packet, at Claude Sonnet token rates",
      },
      {
        label: "AI usage — at 300 onboardings/month",
        value: "~RM 130–290 / month",
        note: "roughly half with the Batch API for overnight, non-urgent runs",
      },
      {
        label: "Build (one-off)",
        value: "scoped per integration",
        note: "a focused single onboarding flow is a few weeks of engineering; we quote fixed after a short discovery",
      },
      {
        label: "Ongoing",
        value: "monitoring + support",
        note: "small next to the manual-review time it removes and the drop-offs it recovers",
      },
    ],
    disclaimer:
      "Indicative only, shown in Ringgit at roughly RM 4.09 to the USD (mid-market, 11 Aug 2026); per-token rates follow Anthropic's published pricing (confirm current figures). Screening list access is priced by your provider, separate from AI usage. Actual AI usage depends on document sizes and monthly volume. You can model the same figures in SGD or your currency with our Claude cost calculator.",
  },
  howWeBuild:
    "It's wired straight into your onboarding intake and screening provider through connected tools — no manual exports — and it works from the applicant's live case record, not a stale copy, so a re-submitted document updates the packet. Guardrails and a full audit trail are built in from day one: every classification and every flag traces back to the exact document and field it came from, and no account opens without your officer's decision.",
  agentLoop: [
    "the document bundle & the applicant's case record",
    "classify, route & screen (MCP tools)",
    "re-check the match & screening result before surfacing",
  ],
  integrations: [
    "Onboarding intake / origination system",
    "Document store or upload inbox",
    "Sanctions / PEP screening provider",
    "Optional: core banking or policy admin system (read-only)",
  ],
  modelChoice: {
    approach: "Single model with tiered routing",
    rationale:
      "Onboarding is heterogeneous — different document types need different checks — so we route with a classifier and lean on tiering (Haiku for the obvious, Sonnet for reasoning, Opus for the borderline), not a multi-agent swarm. One well-instrumented, tiered pipeline is reliable, cheaper and easier to audit.",
    costNote:
      "We reserve multi-agent designs (≈15× the tokens) for genuinely parallel, breadth-first work — not for a single applicant's tightly-coupled file.",
  },
  evaluation: [
    "Deterministic checks first — document classified correctly, fields match, required documents present",
    "Self-verification — the agent re-checks its match and screening result before surfacing them",
    "Business KPIs — reviewer time per applicant, onboarding drop-off, consistency, and true hits caught",
  ],
  evaluationDetail: {
    framework:
      "We score the agent with the Agent GPA framework — Goal, Plan, Action, a current standard for agent reliability — on top of the human baseline.",
    baseline:
      "First we measure the current manual onboarding on a sample — time per applicant, how consistently two officers reach the same conclusion, and how many mismatches or screening hits slip through. That human baseline is what every agent metric is measured against, so the lift is provable rather than assumed.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Did it reach the right outcome?",
        metrics: [
          {
            name: "Agreement with a senior compliance officer",
            measures:
              "Its packet, flags and recommended action match an expert's on held-out onboardings",
            target: "≥ 90%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Was the approach sound and complete?",
        metrics: [
          {
            name: "Required checks executed",
            measures: "Every mandated CDD check — identity, address, screening — is run, nothing skipped",
            target: "100%",
          },
          {
            name: "Correct routing",
            measures: "Each document is dispatched to the right check for its type",
            target: "≥ 99%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Were the individual steps correct?",
        metrics: [
          {
            name: "Document classification accuracy",
            measures: "Each document is identified as the right type",
            target: "≥ 98%",
          },
          {
            name: "Field extraction accuracy",
            measures: "Did it read each field — name, IC/passport, address — correctly?",
            target: "≥ 98%",
          },
          {
            name: "Screening recall (true-hit catch rate)",
            measures:
              "Of the real sanctions/PEP hits in the golden set, the share it surfaces — any miss is a release-blocker, and every possible match is escalated to the officer, never auto-cleared.",
            target: "≥ 99% (goal: no missed hit)",
          },
          {
            name: "No hallucinated details",
            measures: "Every field in the packet traces to a source document",
            target: "0 tolerated",
          },
        ],
      },
    ],
    gate: "The agent does not touch a live applicant until it beats the human baseline and clears these GPA targets on a golden set of historical onboardings — including impersonation attempts and true screening hits. Every change is regression-tested against the same set before it ships.",
  },
  delivery:
    "We start with a proof-of-concept on your real historical onboardings — including the messy scans, mismatches and true screening hits — and prove reliability before it touches a live applicant. A focused single-flow rollout is a matter of weeks.",
  ctaTitle: "Is your compliance team classifying and matching KYC documents by hand?",
  ctaDescription:
    "Tell us your onboarding flow and volumes and we'll tell you honestly whether an onboarding & KYC agent is worth building — and what it would take.",
  faqs: [
    {
      question: "Does the agent open accounts or approve customers?",
      answer:
        "No. It classifies, verifies, screens and flags; a human compliance officer makes the accept, refer or reject decision. Human-in-the-loop is essential for regulated onboarding in Malaysia.",
    },
    {
      question: "How is it kept compliant with BNM/MAS AML/CFT and PDPA/PDPC?",
      answer:
        "With a full audit trail so every onboarding decision can be reconstructed, screening evidence retained, and PDPA/PDPC-aligned handling of where identity data is processed, stored and accessed. Treat this as a starting point for your compliance team, not legal advice.",
    },
    {
      question: "Can it screen against sanctions and PEP lists?",
      answer:
        "Yes — it connects to your screening provider as a read-only lookup and retains the evidence for each result. A possible match is always escalated to your officer, never cleared automatically.",
    },
    {
      question: "How long does it take to deploy?",
      answer:
        "A proof-of-concept for one focused onboarding flow is usually a matter of weeks; a full rollout takes longer, mostly because of integration and getting officers comfortable, not the AI.",
    },
  ],
};

const ms: UseCaseContent = {
  industry: "Perkhidmatan kewangan & onboarding terkawal",
  title: "Ejen onboarding & KYC",
  cardSummary:
    "Semak bahawa dokumen pengenalan, bukti alamat dan butiran permohonan adalah konsisten.",
  heroSubhead:
    "Kami mereka bentuk, membina dan melaksanakan ejen AI yang mengelaskan setiap dokumen onboarding, menghalakannya ke semakan yang betul, menyelaraskan identiti, alamat dan borang permohonan, dan menyediakan pakej KYC yang bersih dan berbukti untuk pegawai pematuhan anda — sementara keputusan terima-atau-rujuk kekal dengan manusia.",
  snapshot: {
    valueLabel: "Tinggi",
    valueWhy:
      "Onboarding ialah tempat urus niaga tersekat dan tempat risiko AML/CDD masuk — pengesahan yang lebih pantas dan konsisten memenangi pelanggan yang anda hilang kerana pendaftaran perlahan dan menutup jurang yang dicari audit BNM atau MAS.",
    complexityLabel: "Sederhana–tinggi",
    complexityWhy:
      "Bahagian hadapan routing ke semakan setiap dokumen, visi/OCR pada MyKad/NRIC dan bil utiliti, semakan sekatan/PEP, serta kawalan dan jejak audit bertaraf BNM/MAS AML/CFT + PDPA/PDPC.",
    timeToLive: "~beberapa minggu (satu aliran onboarding)",
    bestFit: "Bank, fintech, penanggung insurans & mana-mana perniagaan terkawal yang menerima pelanggan baharu",
  },
  outcomes: [
    "Memenangi pelanggan yang anda hilang hari ini kerana pendaftaran manual yang perlahan",
    "Menangkap ketidaksepadanan dokumen dan identiti sebelum akaun dibuka",
    "Semakan CDD yang sama diterapkan secara konsisten pada setiap pemohon",
    "Lebih banyak onboarding diselesaikan tanpa menambah kakitangan pematuhan",
  ],
  impact: {
    manualLabel: "Semakan KYC manual hari ini",
    manualValue: "~20–30 min",
    agentLabel: "Dengan ejen",
    agentValue: "~4–7 min",
    saved:
      "≈ 15–23 minit dijimatkan setiap pemohon — kira-kira 75–115 jam sebulan pada 300 onboarding, lebih kurang separuh penyemak sepenuh masa. Dan setiap pemohon yang diselesaikan dalam minit dan bukan hari ialah pelanggan yang anda kekalkan, bukan hilang kepada pendaftaran pesaing yang lebih pantas.",
    note: "Anggaran; masa sebenar bergantung pada bilangan dokumen, kedalaman saringan dan kerumitan kes.",
  },
  businessProblem:
    "Sebuah bank, penanggung insurans atau fintech di rantau ini menerima setiap pelanggan baharu sebagai satu himpunan — MyKad/NRIC atau pasport, swafoto, bukti alamat, dan borang permohonan — kemudian pegawai pematuhan perlu mengesahkan identiti, memastikan alamat, menyaring terhadap senarai sekatan dan PEP, dan menyemak setiap medan sepadan sebelum akaun boleh dibuka. Sebahagian besar masa itu dihabiskan pada bahagian mekanikal: mengelaskan dokumen dan memadankannya antara satu sama lain, bukan menilai risiko. Ia perlahan, pemohon berputus asa sementara menunggu, dan dua pegawai menerapkan usaha wajar yang berbeza pada fail yang sama — ketidakkonsistenan yang ditandakan oleh semakan BNM atau MAS AML/CFT.",
  manualWorkflow: {
    steps: [
      "Buka himpunan onboarding dan setiap dokumen",
      "Fikirkan apa itu setiap dokumen dan baca medan secara manual",
      "Semak silang nama, nombor IC/pasport dan alamat merentas dokumen",
      "Saring pemohon terhadap senarai sekatan dan PEP",
      "Kejar dokumen yang hilang, imbasan buruk atau percanggahan",
      "Barulah membuat keputusan terima, rujuk atau tolak",
    ],
    breaks:
      "Halangan ialah mengelas dan memadan dokumen, bukan menilai risiko — bahagian perlahan dan berulang yang tersekat pada volum tinggi, hilang ketepatan apabila letih, dan berbeza antara pegawai.",
  },
  agenticWorkflow: {
    narrative:
      "Kami membinanya sebagai aliran routing: ejen mengelaskan setiap dokumen masuk, menghantarnya ke semakan khusus yang betul (identiti, alamat, saringan), kemudian menyelaraskan keputusan ke dalam satu pakej KYC berbukti — meminta semula hanya apa yang benar-benar hilang dan mengangkat apa-apa yang tidak dapat diselesaikan kepada manusia.",
    steps: [
      "Kelaskan setiap dokumen (MyKad/NRIC, pasport, bil, borang)",
      "Halakan ke semakan betul (identiti / alamat / saringan)",
      "Ekstrak & sahkan medan yang penting",
      "Padan silang & saring terhadap senarai sekatan/PEP",
      "Himpunkan pakej KYC berbukti untuk pegawai",
    ],
    inLabel: "Pemohon menghantar dokumen mereka",
    outLabel: "Pakej KYC sedia-keputusan",
    loopAround: {
      index: 1,
      label: "Menghala semula dan meminta semula hanya apabila dokumen tersalah kelas, tidak boleh dibaca atau hilang — jika tidak, ia terus bergerak.",
    },
    humanStep: "Pegawai pematuhan membuat keputusan terima / rujuk / tolak",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "Dalam himpunan onboarding",
    inMessage:
      "Borang permohonan: alamat 12, Jalan Ampang, 50450 Kuala Lumpur. MyKad/NRIC: Ahmad bin Ismail, 880101-14-5523. Bil utiliti (TNB): 88, Jalan Bukit Bintang, 55100 Kuala Lumpur.",
    outLabel: "Apa yang ejen tandakan",
    outLines: [
      "Nama & nombor IC sepadan merentas MyKad/NRIC dan borang permohonan",
      "Saringan sekatan & PEP: tiada padanan",
      "Dokumen bukti alamat ada dan boleh dibaca",
    ],
    outResult:
      "1 percanggahan ditandakan: alamat pada borang (Jalan Ampang) tidak sepadan dengan bil utiliti (Jalan Bukit Bintang)",
    note: "Setiap tanda memetik dokumen & medan tepat · pegawai anda membuat keputusan terima/rujuk · ~RM 0.50–1.10 setiap pemohon.",
  },
  workflowDetail: [
    {
      title: "Kelaskan setiap dokumen",
      detail:
        "Ejen membaca setiap fail dalam himpunan dan memikirkan apa itu setiap satu — MyKad/NRIC, pasport, swafoto, bil utiliti, penyata bank, borang permohonan — termasuk imbasan dan foto telefon, supaya semakan betul dijalankan pada dokumen yang betul.",
    },
    {
      title: "Halakan ke semakan yang betul",
      detail:
        "Setiap dokumen dihantar ke pengendali khususnya: dokumen identiti ke pengesahan identiti, bukti alamat ke pemadanan alamat, butiran pemohon ke saringan sekatan dan PEP — routing yang mengekalkan setiap semakan fokus dan tepat.",
    },
    {
      title: "Ekstrak dan sahkan medan",
      detail:
        "Ia menarik medan penting ke dalam rekod berstruktur — nama, nombor IC/pasport, tarikh lahir, alamat, kewarganegaraan — menandakan dokumen sumber bagi setiap satu, dan menyemak dokumen boleh dibaca serta sah tempoh.",
    },
    {
      title: "Padan silang dan saring",
      detail:
        "Ia menyelaraskan medan tersebut antara satu sama lain — nama dan IC pada MyKad/NRIC vs borang, alamat pada bil vs borang — dan menyaring pemohon terhadap senarai pemerhatian sekatan dan PEP, menyimpan bukti bagi setiap keputusan.",
    },
    {
      title: "Himpunkan pakej KYC",
      detail:
        "Ia menghasilkan pakej satu halaman: pemohon, semakan yang lulus, keputusan saringan, dan dua atau tiga perkara untuk dilihat — setiap satu dengan butiran dan dokumen sumbernya.",
    },
    {
      title: "Serah kepada manusia",
      detail:
        "Pegawai pematuhan menyemak perkara yang ditandakan dan membuat keputusan terima, rujuk atau tolak. Tiada apa-apa diluluskan secara automatik.",
    },
  ],
  architecture: [
    "Corak: aliran routing atas Claude Agent SDK — bahagian hadapan pengelas menghantar setiap dokumen ke rantaian semakan setiap jenis, setiap alat didedahkan melalui pelayan MCP dalam-proses.",
    "Konteks: seluruh himpunan dimuatkan — teks serta visi untuk MyKad/NRIC, pasport dan foto bil utiliti — dengan rekod kes langsung pemohon ditarik segar (bukan salinan lapuk) dan penyerahan panjang dimampatkan supaya tiada yang tercicir secara senyap.",
    "Alat: pengelas dokumen, pengekstrak medan visi/OCR, pemadan alamat/identiti, dan semakan saringan sekatan/PEP baca-sahaja — alat baca dipisahkan daripada sebarang tulis, yang hanya ke baris gilir semakan.",
    "Pagar keselamatan: output JSON terkunci-skema, tiada akses tulis ke sistem perbankan teras / pentadbiran polisi, keistimewaan-minimum, dan peraturan tegas bahawa tiada akaun dibuka tanpa keputusan terima manusia.",
    "Input tidak dipercayai: setiap dokumen diserahkan dianggap tidak dipercayai (tahan suntikan-prompt) — PDF yang diusik atau kapsyen dalam imej tidak boleh mengemudi pengelasan atau keputusan ejen.",
    "Pemulihan & angkat: dokumen tersalah kelas, tidak boleh dibaca atau hilang dihala semula atau diminta semula; keyakinan saringan rendah atau padanan samar diangkat kepada pegawai — ia tidak pernah meneka identiti atau keputusan saringan.",
    "Kebolehlihatan & audit: setiap pengelasan, panggilan alat, padanan saringan dan pakej akhir dilog dan boleh dijejak, dengan keputusan pegawai direkod terhadapnya — jejak audit BNM/MAS/PDPA boleh-dibaca-mesin, bukan tangkap skrin.",
    "Rangka penilaian: set emas onboarding sejarah — termasuk cubaan menyamar, alamat tidak sepadan dan padanan sekatan sebenar — dengan semakan deterministik serta LLM-sebagai-hakim untuk panggilan kabur, diuji regresi pada setiap perubahan sebelum menyentuh pemohon langsung.",
  ],
  models: [
    {
      task: "Kelaskan dokumen & ekstrak medan (termasuk imbasan)",
      model: "Claude Sonnet",
      why: "Visi boleh dipercayai dan ekstrak berstruktur pada foto MyKad/NRIC, pasport dan bil utiliti yang berselerak.",
    },
    {
      task: "Halakan dokumen bervolum tinggi dan jelas",
      model: "Claude Haiku",
      why: "Paling murah dan pantas untuk sebahagian besar pengelasan jelas dan semakan medan mudah.",
    },
    {
      task: "Selaraskan & menaakul merentas himpunan",
      model: "Claude Sonnet",
      why: "Kuda kerja — penaakulan kuat pada kos sederhana; mengendalikan padan silang dan semakan saringan.",
    },
    {
      task: "Menghakimi kes samar / sempadan",
      model: "Claude Opus",
      why: "Digunakan hanya apabila Sonnet menandakan keyakinan rendah atau kemungkinan padanan saringan — sebahagian kecil pemohon.",
    },
  ],
  cost: {
    rows: [
      {
        label: "Penggunaan AI — setiap pemohon",
        value: "~RM 0.50–1.10",
        note: "dokumen input + pengelasan + penaakulan + pakej output, pada kadar token Claude Sonnet",
      },
      {
        label: "Penggunaan AI — pada 300 onboarding/bulan",
        value: "~RM 130–290 / bulan",
        note: "lebih kurang separuh dengan Batch API untuk larian bukan segera pada waktu malam",
      },
      {
        label: "Pembinaan (sekali)",
        value: "diskop mengikut integrasi",
        note: "satu aliran onboarding yang fokus adalah beberapa minggu kejuruteraan; kami beri sebut harga tetap selepas penemuan ringkas",
      },
      {
        label: "Berterusan",
        value: "pemantauan + sokongan",
        note: "kecil berbanding masa semakan manual yang dihapuskan dan pemohon yang diselamatkan",
      },
    ],
    disclaimer:
      "Anggaran sahaja, ditunjukkan dalam Ringgit pada kira-kira RM 4.09 kepada USD (kadar pasaran, 11 Ogos 2026); kadar setiap token mengikut harga yang diterbitkan Anthropic (sahkan angka semasa). Akses senarai saringan dikenakan harga oleh penyedia anda, berasingan daripada penggunaan AI. Penggunaan AI sebenar bergantung pada saiz dokumen dan volum bulanan. Anda boleh anggarkan angka yang sama dalam SGD atau mata wang anda dengan kalkulator kos Claude kami.",
  },
  howWeBuild:
    "Ia disambung terus ke intake onboarding dan penyedia saringan anda melalui alat yang disambung — tanpa eksport manual — dan ia bekerja daripada rekod kes langsung pemohon, bukan salinan lapuk, jadi dokumen yang diserah semula mengemas kini pakej. Pagar keselamatan dan jejak audit penuh dibina dari hari pertama: setiap pengelasan dan setiap tanda dijejak kembali ke dokumen dan medan tepat asalnya, dan tiada akaun dibuka tanpa keputusan pegawai anda.",
  agentLoop: [
    "himpunan dokumen & rekod kes pemohon",
    "kelas, hala & saring (alat MCP)",
    "semak semula padanan & keputusan saringan sebelum memaparkan",
  ],
  integrations: [
    "Sistem intake / asal onboarding",
    "Simpanan dokumen atau peti masuk muat naik",
    "Penyedia saringan sekatan / PEP",
    "Pilihan: sistem perbankan teras atau pentadbiran polisi (baca-sahaja)",
  ],
  modelChoice: {
    approach: "Model tunggal dengan routing berperingkat",
    rationale:
      "Onboarding adalah heterogen — jenis dokumen berbeza memerlukan semakan berbeza — jadi kami menghala dengan pengelas dan bergantung pada peringkat (Haiku untuk yang jelas, Sonnet untuk penaakulan, Opus untuk yang sempadan), bukan kumpulan berbilang-ejen. Satu saluran berperingkat yang lengkap adalah boleh dipercayai, lebih murah dan lebih mudah diaudit.",
    costNote:
      "Kami menyimpan reka bentuk berbilang-ejen (≈15× token) untuk kerja yang benar-benar selari — bukan untuk satu fail pemohon yang berkait rapat.",
  },
  evaluation: [
    "Semakan deterministik dahulu — dokumen dikelas dengan betul, medan sepadan, dokumen diperlukan hadir",
    "Pengesahan sendiri — ejen menyemak semula padanan dan keputusan saringannya sebelum memaparkan",
    "KPI perniagaan — masa penyemak setiap pemohon, keciciran onboarding, ketekalan, dan padanan sebenar tertangkap",
  ],
  evaluationDetail: {
    framework:
      "Kami menilai ejen dengan rangka kerja Agent GPA — Goal, Plan, Action, satu standard semasa untuk kebolehpercayaan ejen — di atas garis dasar manusia.",
    baseline:
      "Mula-mula kami mengukur onboarding manual semasa pada sampel — masa setiap pemohon, sejauh mana konsisten dua pegawai mencapai keputusan sama, dan berapa banyak ketidaksepadanan atau padanan saringan terlepas. Garis dasar manusia itu menjadi ukuran untuk setiap metrik ejen, jadi peningkatan boleh dibuktikan, bukan diandaikan.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Adakah ia mencapai hasil yang betul?",
        metrics: [
          {
            name: "Persetujuan dengan pegawai pematuhan kanan",
            measures: "Pakej, tanda dan tindakan disyorkannya sepadan dengan pakar pada onboarding terpencil",
            target: "≥ 90%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Adakah pendekatannya kukuh dan lengkap?",
        metrics: [
          {
            name: "Semakan diperlukan dilaksanakan",
            measures: "Setiap semakan CDD wajib — identiti, alamat, saringan — dijalankan, tiada dilangkau",
            target: "100%",
          },
          {
            name: "Routing betul",
            measures: "Setiap dokumen dihantar ke semakan yang betul bagi jenisnya",
            target: "≥ 99%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Adakah setiap langkah betul?",
        metrics: [
          {
            name: "Ketepatan pengelasan dokumen",
            measures: "Setiap dokumen dikenal pasti sebagai jenis yang betul",
            target: "≥ 98%",
          },
          {
            name: "Ketepatan ekstrak medan",
            measures: "Adakah ia membaca setiap medan — nama, IC/pasport, alamat — dengan betul?",
            target: "≥ 98%",
          },
          {
            name: "Recall saringan (kadar tangkap padanan sebenar)",
            measures:
              "Daripada padanan sekatan/PEP sebenar dalam set emas, berapa banyak yang dipaparkan — sebarang yang terlepas menyekat pelepasan, dan setiap kemungkinan padanan diangkat kepada pegawai, tidak pernah dibersihkan secara automatik.",
            target: "≥ 99% (matlamat: tiada padanan terlepas)",
          },
          {
            name: "Tiada butiran halusinasi",
            measures: "Setiap medan dalam pakej boleh dijejak ke dokumen sumber",
            target: "0 dibenarkan",
          },
        ],
      },
    ],
    gate: "Ejen tidak menyentuh pemohon langsung sehingga ia mengatasi garis dasar manusia dan mencapai sasaran GPA ini pada set emas onboarding sejarah — termasuk cubaan menyamar dan padanan saringan sebenar. Setiap perubahan diuji regresi terhadap set yang sama sebelum dihantar.",
  },
  delivery:
    "Kami bermula dengan bukti-konsep atas onboarding sejarah sebenar anda — termasuk imbasan berselerak, ketidaksepadanan dan padanan saringan sebenar — dan membuktikan kebolehpercayaan sebelum ia menyentuh pemohon langsung. Pelancaran satu aliran yang fokus mengambil masa beberapa minggu.",
  ctaTitle: "Adakah pasukan pematuhan anda mengelas dan memadan dokumen KYC secara manual?",
  ctaDescription:
    "Beritahu kami aliran onboarding dan volum anda, dan kami akan beritahu dengan jujur sama ada ejen onboarding & KYC berbaloi dibina — dan apa yang diperlukan.",
  faqs: [
    {
      question: "Adakah ejen membuka akaun atau meluluskan pelanggan?",
      answer:
        "Tidak. Ia mengelas, mengesah, menyaring dan menandakan; pegawai pematuhan manusia membuat keputusan terima, rujuk atau tolak. Manusia-dalam-gelung penting untuk onboarding terkawal di Malaysia.",
    },
    {
      question: "Bagaimana ia mematuhi BNM/MAS AML/CFT dan PDPA/PDPC?",
      answer:
        "Dengan jejak audit penuh supaya setiap keputusan onboarding boleh dibina semula, bukti saringan disimpan, dan pengendalian selaras PDPA/PDPC tentang di mana data identiti diproses, disimpan dan diakses. Anggap ini sebagai titik permulaan untuk pasukan pematuhan anda, bukan nasihat undang-undang.",
    },
    {
      question: "Bolehkah ia menyaring terhadap senarai sekatan dan PEP?",
      answer:
        "Boleh — ia bersambung ke penyedia saringan anda sebagai semakan baca-sahaja dan menyimpan bukti bagi setiap keputusan. Kemungkinan padanan sentiasa diangkat kepada pegawai anda, tidak pernah diluluskan secara automatik.",
    },
    {
      question: "Berapa lama untuk dilaksanakan?",
      answer:
        "Bukti-konsep untuk satu aliran onboarding yang fokus biasanya beberapa minggu; pelancaran penuh mengambil masa lebih lama, kebanyakannya kerana integrasi dan membiasakan pegawai, bukan AI.",
    },
  ],
};

const zh: UseCaseContent = {
  industry: "金融服务与受监管开户",
  title: "开户与 KYC 智能体",
  cardSummary: "核对身份证件、地址证明与申请信息是否一致。",
  heroSubhead:
    "我们设计、构建并部署一个 AI 智能体，对每份开户文件进行分类、路由到对应的检查，核对身份、地址与申请表格，并为你的合规主管提供一份干净、有证据支撑的 KYC 材料包——而接受或转介的决定仍由人来做。",
  snapshot: {
    valueLabel: "高",
    valueWhy:
      "开户正是交易受阻、AML/CDD 风险进入之处——更快、更一致的验证能赢回因注册缓慢而流失的客户，并堵住 BNM 或 MAS 审计所关注的漏洞。",
    complexityLabel: "中偏高",
    complexityWhy:
      "路由前端接入按文件类型的检查、对 MyKad/NRIC 与水电费单的视觉/OCR、制裁/PEP 名单筛查，以及 BNM/MAS AML/CFT + PDPA/PDPC 级的管控与审计轨迹。",
    timeToLive: "约数周（单一开户流程）",
    bestFit: "银行、金融科技、保险公司及任何为新客户开户的受监管企业",
  },
  outcomes: [
    "赢回你如今因缓慢的人工注册而流失的客户",
    "在账户开立前捕获文件与身份的不一致",
    "对每位申请人一致地应用同样的 CDD 检查",
    "在不增加合规人手的情况下完成更多开户",
  ],
  impact: {
    manualLabel: "当前人工 KYC 审核",
    manualValue: "约 20–30 分钟",
    agentLabel: "使用智能体",
    agentValue: "约 4–7 分钟",
    saved:
      "每位申请人约节省 15–23 分钟——每月 300 次开户约合 75–115 小时，大约相当于半名全职审核人员。而每一位在几分钟而非几天内通过的申请人，都是你留住的客户，而非流失给注册更快的对手。",
    note: "为估算；实际时间取决于文件数量、筛查深度与案件复杂度。",
  },
  businessProblem:
    "区域内的银行、保险公司或金融科技公司将每位新客户作为一整套材料接收——MyKad/NRIC 或护照、一张自拍、地址证明，以及申请表格——然后合规主管必须验证身份、确认地址、对照制裁与 PEP 名单筛查，并在账户开立前核对每个字段是否一致。这段时间的大部分花在机械性工作上：对文件分类并相互比对，而非判断风险。这个过程缓慢，申请人在等待中流失，两名主管对同一份文件的尽职程度也略有不同——正是 BNM 或 MAS AML/CFT 审查所标记的那种不一致。",
  manualWorkflow: {
    steps: [
      "打开开户材料及每份文件",
      "弄清每份文件是什么，并用肉眼阅读字段",
      "跨文件交叉核对姓名、身份证/护照号与地址",
      "对照制裁与 PEP 名单筛查申请人",
      "追查缺失文件、扫描不清或不一致之处",
      "之后才做接受、转介或拒绝的决定",
    ],
    breaks:
      "瓶颈在于对文件分类和比对，而非判断风险——这个缓慢重复的环节在量大时堵塞、疲劳时出错、且因人而异。",
  },
  agenticWorkflow: {
    narrative:
      "我们以 routing（路由）方式构建：智能体对每份进入的文件分类，将其分派到正确的专门检查（身份、地址、筛查），再把结果核对汇总为一份有证据支撑的 KYC 材料包——只重新索取确实缺失的部分，无法通过的则上报给人处理。",
    steps: [
      "对每份文件分类（MyKad/NRIC、护照、账单、表格）",
      "路由到正确的检查（身份 / 地址 / 筛查）",
      "提取并验证关键字段",
      "交叉比对并对照制裁/PEP 名单筛查",
      "为主管汇总一份有证据的 KYC 材料包",
    ],
    inLabel: "申请人提交其文件",
    outLabel: "一份可直接决策的 KYC 材料包",
    loopAround: {
      index: 1,
      label: "仅当文件被错误分类、无法辨读或缺失时才重新路由并重新索取——否则持续推进。",
    },
    humanStep: "合规主管做出接受 / 转介 / 拒绝的决定",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "在开户材料中",
    inMessage:
      "申请表：地址 12, Jalan Ampang, 50450 Kuala Lumpur。MyKad/NRIC：Ahmad bin Ismail，880101-14-5523。水电费单（TNB）：88, Jalan Bukit Bintang, 55100 Kuala Lumpur。",
    outLabel: "智能体标记出什么",
    outLines: [
      "姓名与身份证号在 MyKad/NRIC 与申请表间一致",
      "制裁与 PEP 筛查：无匹配",
      "地址证明文件齐全且清晰可读",
    ],
    outResult:
      "标记 1 处差异：表格上的地址（Jalan Ampang）与水电费单（Jalan Bukit Bintang）不一致",
    note: "每个标记都引用确切的文件与字段 · 由你的主管做接受/转介决定 · 每位申请人约 RM 0.50–1.10。",
  },
  workflowDetail: [
    {
      title: "对每份文件分类",
      detail:
        "智能体读取材料中的每份文件并弄清各自是什么——MyKad/NRIC、护照、自拍、水电费单、银行对账单、申请表格——包括扫描件和手机照片，从而让正确的检查在正确的文件上运行。",
    },
    {
      title: "路由到正确的检查",
      detail:
        "每份文件被分派到其专门处理器：身份证件到身份验证，地址证明到地址比对，申请人信息到制裁与 PEP 筛查——正是这种路由让每项检查保持聚焦和准确。",
    },
    {
      title: "提取并验证字段",
      detail:
        "它将关键字段提取为一条结构化记录——姓名、身份证/护照号、出生日期、地址、国籍——为每一项标注来源文件，并检查文件清晰可读且在有效期内。",
    },
    {
      title: "交叉比对并筛查",
      detail:
        "它将这些字段相互核对——MyKad/NRIC 上的姓名与身份证号 vs 表格、账单上的地址 vs 表格——并对照制裁与 PEP 监控名单筛查申请人，为每个结果保留证据。",
    },
    {
      title: "汇总 KYC 材料包",
      detail:
        "它生成一页式材料包：申请人、通过的检查、筛查结果、以及需要关注的两三项——每一项都附带具体信息及其来源文件。",
    },
    {
      title: "交给人处理",
      detail:
        "合规主管审阅被标记的项目并做出接受、转介或拒绝的决定。没有任何内容会被自动批准。",
    },
  ],
  architecture: [
    "模式：基于 Claude Agent SDK 的路由工作流——分类器前端将每份文件分派到按类型的检查链，每个工具通过进程内 MCP 服务器提供。",
    "上下文：整份材料被载入——文本加上对 MyKad/NRIC、护照与水电费单照片的视觉识别——申请人的实时案件记录被实时拉取（而非过期副本），较长的提交会被压缩，确保没有内容被静默丢弃。",
    "工具：文档分类器、视觉/OCR 字段提取器、地址/身份匹配器，以及一个只读的制裁/PEP 筛查查询——读工具与任何写入分离，写入仅进入审阅队列。",
    "防护机制：严格的 JSON 模式锁定输出、对核心银行/保单管理系统无写入权限、最小权限，以及一条硬性规则——没有人工接受决定，任何账户都不会开立。",
    "不可信输入：每份提交的文件都被视为不可信（可抵御提示注入）——被篡改的 PDF 或图像内的文字说明无法左右智能体的分类或决定。",
    "恢复与上报：被错误分类、无法辨读或缺失的文件会被重新路由或重新索取；筛查置信度低或匹配含糊则上报给主管——它绝不臆测身份或筛查结果。",
    "可观测性与审计：每次分类、工具调用、筛查命中与最终材料包都会留痕且可追溯，主管的决定对应记录于其上——一条机器可读的 BNM/MAS/PDPA 审计轨迹，而非截图。",
    "评估框架：一组历史开户案例的黄金集——含冒名顶替尝试、地址不一致与真实制裁命中——配合确定性检查并对模糊判断采用 LLM 作为评审，每次改动在接触实时申请人前都经过回归测试。",
  ],
  models: [
    {
      task: "对文件分类并提取字段（含扫描件）",
      model: "Claude Sonnet",
      why: "对杂乱的 MyKad/NRIC 照片、护照与水电费单具备可靠的视觉与结构化提取能力。",
    },
    {
      task: "路由高频、明确的文件",
      model: "Claude Haiku",
      why: "对绝大多数一目了然的分类与简单字段检查最便宜、最快。",
    },
    {
      task: "跨材料核对与推理",
      model: "Claude Sonnet",
      why: "主力模型——中等成本下的强推理能力，处理交叉比对与筛查复核。",
    },
    {
      task: "裁决模糊 / 临界案件",
      model: "Claude Opus",
      why: "仅在 Sonnet 标记低置信度或可能的筛查匹配时调用——占申请人的一小部分。",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI 用量——每位申请人",
        value: "约 RM 0.50–1.10",
        note: "输入文件 + 分类 + 推理 + 输出材料包，按 Claude Sonnet 的 token 费率",
      },
      {
        label: "AI 用量——每月 300 次开户",
        value: "约 RM 130–290 / 月",
        note: "使用 Batch API 处理非紧急的夜间任务约可减半",
      },
      {
        label: "构建（一次性）",
        value: "按集成范围报价",
        note: "聚焦单一开户流程的智能体约需数周工程；简短的需求梳理后我们给出固定报价",
      },
      {
        label: "持续运营",
        value: "监控 + 支持",
        note: "相比其省去的人工审核时间与挽回的流失客户，成本很小",
      },
    ],
    disclaimer:
      "仅为估算，按约 1 美元兑 4.09 令吉（市场中间价，2026 年 8 月 11 日）以令吉显示；每 token 费率以 Anthropic 公布的定价为准（请核对最新数字）。筛查名单的访问由你的供应商单独计价，与 AI 用量分开。实际 AI 用量取决于文件大小与每月业务量。你可以用我们的 Claude 成本计算器把同样的数字换算成 SGD 或你的货币。",
  },
  howWeBuild:
    "它通过连接的工具直接接入你的开户受理系统与筛查供应商——无需手动导出——并且它基于申请人的实时案件记录工作，而非过期副本，因此重新提交的文件会更新材料包。防护机制与完整审计轨迹从第一天就内建：每一次分类和每一个标记都能追溯到其来源的确切文件与字段，且没有你主管的决定，任何账户都不会开立。",
  agentLoop: [
    "文件材料与申请人的案件记录",
    "分类、路由并筛查（MCP 工具）",
    "在呈现前复核比对与筛查结果",
  ],
  integrations: [
    "开户受理 / 发起系统",
    "文件库或上传收件箱",
    "制裁 / PEP 筛查供应商",
    "可选：核心银行或保单管理系统（只读）",
  ],
  modelChoice: {
    approach: "单一模型加分层路由",
    rationale:
      "开户是异构的——不同文件类型需要不同的检查——因此我们用分类器进行路由，并依靠分层（Haiku 处理明确的、Sonnet 负责推理、Opus 处理临界的），而非多智能体群。一条完善配置的分层流水线更可靠、更省成本、也更易审计。",
    costNote:
      "我们把多智能体设计（约 15 倍 token）留给真正并行、广度优先的工作——而非单个申请人紧耦合的案件。",
  },
  evaluation: [
    "先做确定性检查——文件分类正确、字段匹配、必需文件齐全",
    "自我验证——智能体在呈现前复核其比对与筛查结果",
    "业务 KPI——每位申请人的审核时间、开户流失率、一致性、以及拦截到的真实命中",
  ],
  evaluationDetail: {
    framework:
      "我们采用 Agent GPA 框架——Goal、Plan、Action，当前衡量智能体可靠性的标准——并结合人工基线来评估。",
    baseline:
      "我们先在样本上测量当前的人工开户——每位申请人耗时、两名主管得出相同结论的一致程度、以及有多少不一致或筛查命中被漏掉。这个人工基线是衡量每个智能体指标的基准，因此提升是可证明的，而非假设。",
    dimensions: [
      {
        dim: "Goal",
        desc: "是否达成了正确的结果？",
        metrics: [
          {
            name: "与资深合规主管的一致度",
            measures: "在留出开户案例上，其材料包、标记与建议行动是否与专家一致",
            target: "≥ 90%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "方法是否合理且完整？",
        metrics: [
          {
            name: "必需检查全部执行",
            measures: "每一项规定的 CDD 检查——身份、地址、筛查——都执行，不遗漏",
            target: "100%",
          },
          {
            name: "路由正确",
            measures: "每份文件都被分派到与其类型相符的检查",
            target: "≥ 99%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "各个步骤是否正确？",
        metrics: [
          {
            name: "文件分类准确率",
            measures: "每份文件都被识别为正确的类型",
            target: "≥ 98%",
          },
          {
            name: "字段提取准确率",
            measures: "是否正确读取每个字段——姓名、身份证/护照、地址？",
            target: "≥ 98%",
          },
          {
            name: "筛查召回率（真实命中拦截率）",
            measures:
              "黄金集中真实的制裁/PEP 命中，有多少被呈现出来——任何漏报都会阻断上线，且每一个可能的匹配都会上报给主管，绝不自动放行。",
            target: "≥ 99%（目标：不漏任何命中）",
          },
          {
            name: "无凭空捏造的信息",
            measures: "材料包中的每个字段都能追溯到来源文件",
            target: "零容忍",
          },
        ],
      },
    ],
    gate: "在智能体于历史开户黄金集（含冒名顶替尝试与真实筛查命中）上超过人工基线并达到这些 GPA 目标之前，它不会接触任何实时申请人。每次改动在上线前都要针对同一数据集做回归测试。",
  },
  delivery:
    "我们先在你真实的历史开户上做概念验证——包括杂乱的扫描件、不一致与真实的筛查命中——在接触实时申请人前先证明可靠性。聚焦单一流程的落地只需数周。",
  ctaTitle: "你的合规团队还在用人工对 KYC 文件分类和比对吗？",
  ctaDescription:
    "告诉我们你的开户流程和业务量，我们会诚实告诉你开户与 KYC 智能体是否值得构建，以及需要什么。",
  faqs: [
    {
      question: "智能体会开立账户或批准客户吗？",
      answer:
        "不会。它负责分类、验证、筛查和标记；由人类合规主管做出接受、转介或拒绝的决定。在马来西亚的受监管开户中，人在环路中至关重要。",
    },
    {
      question: "如何符合 BNM/MAS AML/CFT 与 PDPA/PDPC 的要求？",
      answer:
        "通过完整的审计轨迹让每一个开户决定可被还原、保留筛查证据，并围绕 PDPA/PDPC 对身份数据处理、存储与访问位置的要求来处理。请将此视为你合规团队的起点，而非法律意见。",
    },
    {
      question: "它能对照制裁与 PEP 名单筛查吗？",
      answer:
        "可以——它以只读查询方式接入你的筛查供应商，并为每个结果保留证据。可能的匹配始终会上报给你的主管，绝不会被自动放行。",
    },
    {
      question: "部署需要多长时间？",
      answer:
        "针对单一聚焦开户流程的概念验证通常只需数周；全面落地需要更长时间，主要是因为集成和让主管适应，而不是 AI 本身。",
    },
  ],
};

export const kycOnboarding: AgentUseCase = {
  structure: {
    slug: "kyc-onboarding",
    status: "available",
    pattern: "routing",
    route: "solutions/agent-use-cases/kyc-onboarding",
    heroImage: "/images/agent-use-cases/kyc-onboarding-hero.jpg",
    cardImage: "/images/agent-use-cases/kyc-onboarding-card.jpg",
    relatedUseCases: ["credit-evaluation", "hr-verification"],
    trackLocation: "agent_use_case_kyc_onboarding",
    valueScore: 4,
    complexityScore: 4,
  },
  content: { en, ms, zh },
};
